import { useEffect, useRef, useState, useCallback } from 'react'

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'retrying' | 'offline'

interface UseAutoSaveOptions {
  /** The data to watch for changes (will be JSON-serialized for comparison) */
  data: unknown
  /** Async function to perform the save */
  onSave: () => Promise<void>
  /** Debounce delay in milliseconds (default: 3000) */
  debounceMs?: number
  /** Whether auto-save is enabled (e.g., false if no dealId) */
  enabled: boolean
  /** How long to show "Saved" status before returning to idle (default: 2000) */
  savedDisplayMs?: number
  /** Max retry attempts before giving up (default: 3) */
  maxRetries?: number
}

interface AutoSaveResult {
  status: AutoSaveStatus
  retryCount: number
  lastError: string | null
  retry: () => void
}

/**
 * Hook that auto-saves data after a debounce period when changes are detected.
 * Includes retry logic with exponential backoff and offline detection.
 *
 * Usage:
 *   const { status, retryCount, lastError, retry } = useAutoSave({
 *     data: { details, selectedProducts },
 *     onSave: async () => { await quotesApi.save(dealId, data) },
 *     enabled: !!dealId,
 *   })
 */
export function useAutoSave({
  data,
  onSave,
  debounceMs = 3000,
  enabled,
  savedDisplayMs = 2000,
  maxRetries = 3,
}: UseAutoSaveOptions): AutoSaveResult {
  const [status, setStatus] = useState<AutoSaveStatus>('idle')
  const [retryCount, setRetryCount] = useState(0)
  const [lastError, setLastError] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedRef = useRef<string>('')
  const isInitialLoadRef = useRef(true)
  const onSaveRef = useRef(onSave)
  const retryCountRef = useRef(0)
  const pendingDataRef = useRef<string | null>(null)

  // Keep onSave ref current without triggering re-renders
  onSaveRef.current = onSave

  // Detect online/offline status
  useEffect(() => {
    const handleOffline = () => setStatus('offline')
    const handleOnline = () => {
      // When coming back online, retry if there was a pending save
      if (pendingDataRef.current) {
        setStatus('retrying')
        performSave()
      } else {
        setStatus('idle')
      }
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  const performSave = useCallback(async () => {
    // Don't attempt save if offline
    if (!navigator.onLine) {
      setStatus('offline')
      return
    }

    try {
      const isRetry = retryCountRef.current > 0
      setStatus(isRetry ? 'retrying' : 'saving')
      await onSaveRef.current()

      // Success — reset retry state
      retryCountRef.current = 0
      setRetryCount(0)
      setLastError(null)
      pendingDataRef.current = null
      setStatus('saved')

      // Clear any existing "saved" display timer
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current)
      }
      savedTimerRef.current = setTimeout(() => {
        setStatus('idle')
      }, savedDisplayMs)
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Save failed'
      console.error(`[AutoSave] Save failed (attempt ${retryCountRef.current + 1}/${maxRetries}):`, error)
      setLastError(errorMsg)

      if (retryCountRef.current < maxRetries - 1) {
        // Schedule retry with exponential backoff: 2s, 4s, 8s
        retryCountRef.current += 1
        setRetryCount(retryCountRef.current)
        setStatus('retrying')

        const backoffMs = Math.pow(2, retryCountRef.current) * 1000
        if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
        retryTimerRef.current = setTimeout(() => {
          performSave()
        }, backoffMs)
      } else {
        // Max retries exhausted — stay in error state (don't auto-clear)
        retryCountRef.current = 0
        setRetryCount(0)
        setStatus('error')
        // Store the unsaved data reference so manual retry knows what to save
        pendingDataRef.current = JSON.stringify(data)
      }
    }
  }, [savedDisplayMs, maxRetries, data])

  // Manual retry function exposed to the UI
  const retry = useCallback(() => {
    retryCountRef.current = 0
    setRetryCount(0)
    setLastError(null)
    performSave()
  }, [performSave])

  // Reset initial load flag when enabled changes (e.g., new session loaded).
  // IMPORTANT: this effect must be defined BEFORE the data-watching effect below
  // so that React runs it first in the same render cycle. Otherwise the reset
  // runs AFTER the data effect, causing the first real user change to be
  // misidentified as an "initial load" and silently skipped.
  useEffect(() => {
    isInitialLoadRef.current = true
    lastSavedRef.current = ''
  }, [enabled])

  useEffect(() => {
    if (!enabled) return

    const serialized = JSON.stringify(data)

    // Skip the initial load (don't save immediately after loading data from DB)
    if (isInitialLoadRef.current) {
      lastSavedRef.current = serialized
      isInitialLoadRef.current = false
      return
    }

    // No change from last saved state — skip
    if (serialized === lastSavedRef.current) return

    // Data changed — schedule a debounced save
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      lastSavedRef.current = serialized
      pendingDataRef.current = serialized
      performSave()
    }, debounceMs)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [data, enabled, debounceMs, performSave])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current)
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    }
  }, [])

  return { status, retryCount, lastError, retry }
}
